import { useState } from "react";
import { toast } from "sonner";
import { useDeleteFacilityPhotoMutation } from "Api/Mutations/Merchants/useDeleteFacilityPhotoMutation";
import { useUploadFacilityPhotosMutation } from "Api/Mutations/Merchants/useUploadFacilityPhotosMutation";
import { useGetFacilityPhotosQuery } from "Api/Queries/Merchants/useGetFacilityPhotosQuery";
import type { PhotoState } from "Components/Shared/Merchant/Facilities/Sections/PhotoInput";
import type { PhotoSlot } from "Components/Shared/Merchant/Facilities/Sections/PhotoSection";
import { toImageDataUrl } from "Components/Shared/Merchant/Facilities/utils";
import {
  FacilityPhotoType,
  type MerchantFacilityDto,
} from "Infrastructure/Api/Api";
import { getApiProblemErrors } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;

type PerSlot<T> = Record<PhotoSlot, T>;

const PHOTO_SLOTS: ReadonlyArray<PhotoSlot> = ["outside", "inside"];
const NO_PENDING_FILES: PerSlot<File | null> = { outside: null, inside: null };
const NO_PHOTO_ERRORS: PerSlot<string | null> = { outside: null, inside: null };

// The backend keys a rejected photo by its request property; the reason identifier
// (fileSizeExceeded / invalidFileType) travels in detail.message. Both are pinned by
// UploadMerchantFacilityPhotosCommandTests.
const PHOTO_SLOT_API_KEY: PerSlot<string> = {
  outside: "PhotoOutside",
  inside: "PhotoInside",
};
const PHOTO_SLOT_TYPE: PerSlot<FacilityPhotoType> = {
  outside: FacilityPhotoType.Outside,
  inside: FacilityPhotoType.Inside,
};
const PHOTO_ERROR_RESOURCES: Record<string, string> = {
  fileSizeExceeded: RESOURCES.PhotoTooLarge,
  invalidFileType: RESOURCES.PhotoInvalidType,
};

/** Resource key for the given slot, or null when the response did not blame that slot. */
const getPhotoSlotErrorResource = (
  error: unknown,
  slot: PhotoSlot,
): string | null => {
  const details = getApiProblemErrors(error)?.[PHOTO_SLOT_API_KEY[slot]];
  if (!details) {
    return null;
  }

  const knownResource = details
    .map(({ message }) =>
      message ? PHOTO_ERROR_RESOURCES[message] : undefined,
    )
    .find((resource) => resource !== undefined);

  return knownResource ?? RESOURCES.PhotoUploadError;
};

type Params = {
  merchantPublicID: string;
  facility?: Partial<MerchantFacilityDto>;
  onUploadRejected?: () => void;
};

/**
 * Owns the facility photo slots of the facility form: the files picked but not yet
 * uploaded, the per-slot error shown under each input, the stored photos preview,
 * and the upload/delete calls.
 */
export const useFacilityPhotos = ({
  facility,
  merchantPublicID,
  onUploadRejected,
}: Params) => {
  const { t } = useResource();
  const facilityPublicID = facility?.publicID;
  const hasExisting: PerSlot<boolean> = {
    outside: !!facility?.hasPhotoOutside,
    inside: !!facility?.hasPhotoInside,
  };

  const [pendingFiles, setPendingFiles] =
    useState<PerSlot<File | null>>(NO_PENDING_FILES);
  // Resource keys, not translated text — translated where rendered, so a language
  // switch while the form is open re-renders the message too.
  const [errorResources, setErrorResources] =
    useState<PerSlot<string | null>>(NO_PHOTO_ERRORS);

  const setPendingPhoto = (slot: PhotoSlot, file: File | null) =>
    setPendingFiles((previous) => ({ ...previous, [slot]: file }));

  const setPhotoError = (slot: PhotoSlot, resource: string | null) =>
    setErrorResources((previous) => ({ ...previous, [slot]: resource }));

  const { data: existingPhotos } = useGetFacilityPhotosQuery(
    merchantPublicID,
    facilityPublicID,
    hasExisting.outside || hasExisting.inside,
  );

  const { mutateAsync: uploadPhotos } = useUploadFacilityPhotosMutation({
    onSuccess: () => toast.success(t(RESOURCES.PhotoUploadSuccess)),
  });

  const { mutate: deletePhotoMutation } = useDeleteFacilityPhotoMutation({
    onError: () => toast.error(t(RESOURCES.PhotoUploadError)),
  });

  /**
   * Uploads the pending photos and reports whether they were saved. On rejection the
   * reason is shown under the blamed slot and that file is dropped so Save re-enables
   * only after a new pick; an unattributed failure (ingress limit, network) keeps both
   * files and marks both slots so the user can simply retry.
   */
  const uploadPendingPhotos = async (
    targetFacilityPublicID: string,
  ): Promise<boolean> => {
    const pendingSlots = PHOTO_SLOTS.filter(
      (slot) => pendingFiles[slot] != null,
    );
    if (pendingSlots.length === 0) {
      return true;
    }
    try {
      await uploadPhotos({
        merchantPublicID,
        facilityPublicID: targetFacilityPublicID,
        photoOutside: pendingFiles.outside,
        photoInside: pendingFiles.inside,
      });
      setErrorResources(NO_PHOTO_ERRORS);
      return true;
    } catch (error) {
      const blamedResources = pendingSlots.map((slot) => ({
        slot,
        resource: getPhotoSlotErrorResource(error, slot),
      }));
      const hasBlamedSlot = blamedResources.some(({ resource }) => !!resource);

      for (const { slot, resource } of blamedResources) {
        if (resource) {
          setPhotoError(slot, resource);
          setPendingPhoto(slot, null);
        } else if (!hasBlamedSlot) {
          setPhotoError(slot, RESOURCES.PhotoUploadError);
        }
      }
      onUploadRejected?.();
      return false;
    }
  };

  const deletePhoto = facilityPublicID
    ? (slot: PhotoSlot) =>
        deletePhotoMutation({
          merchantPublicID,
          facilityPublicID,
          photoType: PHOTO_SLOT_TYPE[slot],
        })
    : undefined;

  const photoOutside: PhotoState = {
    hasExisting: hasExisting.outside,
    existingUrl: toImageDataUrl(
      existingPhotos?.photoOutside,
      existingPhotos?.photoOutsideExtension,
    ),
    pendingFile: pendingFiles.outside,
    errorResource: errorResources.outside,
  };
  const photoInside: PhotoState = {
    hasExisting: hasExisting.inside,
    existingUrl: toImageDataUrl(
      existingPhotos?.photoInside,
      existingPhotos?.photoInsideExtension,
    ),
    pendingFile: pendingFiles.inside,
    errorResource: errorResources.inside,
  };

  const hasPendingOutside = pendingFiles.outside != null;
  const hasPendingInside = pendingFiles.inside != null;

  return {
    photoOutside,
    photoInside,
    hasPendingOutside,
    hasPendingInside,
    hasPendingChanges: hasPendingOutside || hasPendingInside,
    isComplete:
      (hasExisting.outside && hasExisting.inside) ||
      (hasPendingOutside && hasPendingInside),
    setPendingPhoto,
    setPhotoError,
    uploadPendingPhotos,
    deletePhoto,
  };
};
